import { Component, EventEmitter, Input, Output, AfterViewInit, ChangeDetectorRef, OnChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-page-select',
  templateUrl: './page-select.component.html',
  styleUrls: ['./page-select.component.scss'],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'page-select-panel' }
    }
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatIconModule
  ]
})
export class PageSelectComponent implements AfterViewInit, OnChanges {
  @Input() length: number;
  @Input() pageSize: number;
  @Input() pageSizeOptions: number[];
  @Input() selectedPage = 1;
  @Input() sizeMin = false
  @Input() removeBackground = true;

  @Output() itensOutput = new EventEmitter();
  @Output() emitPageSelected = new EventEmitter();

  select = new FormControl();
  pages;
  maxPages: number;
  beforeValue = this.selectedPage;

  /** Opções fixas do input, sem mutação. */
  private basePageSizeOptions: number[] = [];

  /** Opções compatíveis com o total de resultados (e com o valor exibido). */
  get visiblePageSizeOptions(): number[] {
    const base = this.basePageSizeOptions.length
      ? this.basePageSizeOptions
      : (this.pageSizeOptions ?? []);
    const total = this.length || 0;

    if (total <= 0) {
      return [...base];
    }

    const options = base.filter((opt) => opt <= total);
    const current = this.displayedItemCount;

    if (current > 0 && !options.includes(current)) {
      options.push(current);
    }

    return [...new Set(options)].sort((a, b) => a - b);
  }

  /** Quantidade exibida no trigger: compatível com o total de resultados. */
  get displayedItemCount(): number {
    const size = this.pageSize || 0;
    const total = this.length || 0;
    if (total <= 0) return 0;
    return Math.min(size, total);
  }

  constructor(private readonly cdr: ChangeDetectorRef) {
    this.cdr = cdr;
  }

  verifySelectedValue(): void {
    if (this.selectedPage >= 1 && this.selectedPage <= this.maxPages) {
      this.beforeValue = this.selectedPage;
    } else if ((!this.selectedPage && this.selectedPage === 0) || this.selectedPage > this.maxPages) {
      this.selectedPage = this.beforeValue;
    }
  }

  generateMaxPages() {
    this.maxPages = Math.ceil(this.length / this.pageSize);
  }

  ngOnChanges() {
    this.captureBaseOptions();
    this.pagesNumber();
    this.generateMaxPages();
    this.beforeValue = this.selectedPage;
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  private captureBaseOptions(): void {
    if (!Array.isArray(this.pageSizeOptions) || !this.pageSizeOptions.length) {
      this.basePageSizeOptions = [];
      return;
    }

    // Captura uma vez as opções fixas; ignora sufixo dinâmico legado (length no fim).
    if (!this.basePageSizeOptions.length) {
      const options = [...this.pageSizeOptions];
      const last = options[options.length - 1];
      if (options.length > 1 && last === this.length) {
        this.basePageSizeOptions = options.slice(0, -1);
      } else {
        this.basePageSizeOptions = options;
      }
    }
  }

  pagesNumber() {
    this.selectedPage = this.selectedPage === undefined ? 1 : this.selectedPage;

    if (!Array.isArray(this.pageSizeOptions)) {
      this.pages = 1;
      return;
    }

    this.pages = this.pageSize === 0
      ? 1
      : Math.max(1, Math.ceil(this.length / this.pageSize));

    const displaySize = this.displayedItemCount;
    const options = this.visiblePageSizeOptions;
    const value = options.includes(displaySize)
      ? displaySize
      : (options[0] ?? displaySize);

    this.select.setValue(value, { emitEvent: false });
  }

  changePag(op) {
    if (op === '+' && this.selectedPage < this.pages) {
      this.selectedPage++;
      this.emitPageSelected.emit(this.selectedPage);
    } else if (op === '-' && this.selectedPage > 1) {
      this.selectedPage--;
      this.emitPageSelected.emit(this.selectedPage);
    }
    this.beforeValue = this.selectedPage;
  }

  switchToThisPage(idx) {
    if (!idx && idx !== 0) {
      this.selectedPage = 1;
    }
    if (idx >= 1 && idx <= this.maxPages) {
      this.selectedPage = idx;
      this.emitPageSelected.emit(this.selectedPage);
    }
  }

  changeManyItens(itens: number): void {
    this.pageSize = itens;
    this.beforeValue = 1;
    this.selectedPage = this.beforeValue;
    this.pagesNumber();
    this.generateMaxPages();
    this.itensOutput.emit(itens);
  }

  onPageSizeSelectionChange(size: number): void {
    if (size === this.pageSize) return;
    // Selecionar o total atual (menor que o pageSize) não altera a preferência de pageSize.
    if (size === this.length && size < (this.pageSize || 0)) return;
    this.changeManyItens(size);
  }

  styleObject(idx): Object {
    if (this.selectedPage === idx) {
      return {
        'background-color': '#1B4693',
        'color': '#DCE7F9',
        'font-weight': 'bold'
      };
    }
    return {};
  }
}
